import React, { useCallback, useEffect, useRef } from 'react';
import { Box, InputAdornment, TextField, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

interface SlashSearchInputProps {
  value: string;
  onChange: (nextValue: string) => void;
  onClear?: () => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  placeholder?: string;
  enableSlashShortcut?: boolean;
  sx?: SxProps<Theme>;
}

const BASE_FIELD_SX: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    height: 40,
    borderRadius: '12px',
    fontFamily: 'Inter, sans-serif',
    fontSize: '0.875rem',
    lineHeight: 1.4,
    color: '#344054',
    backgroundColor: '#f5f7f9',
    '& fieldset': {
      borderColor: '#edf1f4',
    },
    '&:hover fieldset': {
      borderColor: '#d8e0e6',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#25a8da',
      borderWidth: '1px',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '0.875rem',
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#98a2b3',
    opacity: 1,
  },
};

export const SlashSearchInput: React.FC<SlashSearchInputProps> = ({
  value,
  onChange,
  onClear,
  onFocus,
  placeholder = 'Search',
  enableSlashShortcut = true,
  sx,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const clearValue = useCallback(() => {
    if (onClear) {
      onClear();
      return;
    }
    onChange('');
  }, [onChange, onClear]);

  useEffect(() => {
    if (!enableSlashShortcut) return;

    const handleWindowKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '/' && event.key !== '?') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.defaultPrevented) return;

      const target = event.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      const isEditable =
        tag === 'input' ||
        tag === 'textarea' ||
        tag === 'select' ||
        Boolean(target?.isContentEditable);
      if (isEditable) return;

      if (!inputRef.current) return;

      event.preventDefault();
      focusInput();
    };

    const listenerOptions: AddEventListenerOptions = { capture: true };
    window.addEventListener('keydown', handleWindowKeyDown, listenerOptions);

    return () => {
      window.removeEventListener('keydown', handleWindowKeyDown, listenerOptions);
    };
  }, [enableSlashShortcut, focusInput]);

  return (
    <TextField
      size="small"
      placeholder={placeholder}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key !== 'Escape' || value.length === 0) return;
        event.preventDefault();
        event.stopPropagation();
        clearValue();
      }}
      inputRef={inputRef}
      onFocus={onFocus}
      sx={{ ...BASE_FIELD_SX, ...sx }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start" sx={{ ml: 0.5 }}>
            <SearchRoundedIcon sx={{ color: '#98a2b3', fontSize: 20 }} />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end" sx={{ mr: 0.75 }}>
            <Box
              component="button"
              type="button"
              onMouseDown={(event) => {
                event.preventDefault();
                focusInput();
              }}
              onClick={focusInput}
              aria-label="Focus search"
              sx={{
                borderRadius: '999px',
                border: '1px solid #e4e8ec',
                px: 0.75,
                py: 0.05,
                minWidth: 22,
                textAlign: 'center',
                bgcolor: '#fff',
                cursor: 'text',
              }}
            >
              <Typography
                component="span"
                sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#b0b8c4' }}
              >
                /
              </Typography>
            </Box>
          </InputAdornment>
        ),
      }}
    />
  );
};

export default SlashSearchInput;
